import React, {useCallback, useEffect, useState, useRef} from 'react';
import {View, FlatList, StyleSheet, RefreshControl, TouchableOpacity} from 'react-native';
import {Text, Chip, ActivityIndicator} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {orderApi} from '../../services/api/orderApi';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return {day: String(d.getDate()), month: MONTHS[d.getMonth()]};
};

const getAmountColor = (paymentStatus) => {
  if (paymentStatus === 'PAID') return colors.success;
  if (paymentStatus === 'UNPAID') return colors.error;
  if (paymentStatus === 'PARTIALLY_PAID') return colors.warning;
  return colors.text;
};

const PAGE_SIZE = 20;

const CustomerOrdersScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {customerId, customerName, summaryLabel, summaryAmount, summaryCount} = route.params;

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const loadingMore = useRef(false);

  const fetchPage = useCallback(async (pageNum, replace = false) => {
    try {
      const res = await orderApi.list({customerId, page: pageNum, size: PAGE_SIZE});
      const data = res.data.data;
      const content = data.content || [];
      setOrders(prev => replace ? content : [...prev, ...content]);
      setPage(data.page ?? pageNum);
      setTotalPages(data.totalPages ?? 1);
    } catch (e) {}
  }, [customerId]);

  useEffect(() => {
    setIsLoading(true);
    fetchPage(0, true).finally(() => setIsLoading(false));
  }, [fetchPage]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchPage(0, true);
    setRefreshing(false);
  }, [fetchPage]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore.current) return;
    const nextPage = page + 1;
    if (nextPage < totalPages) {
      loadingMore.current = true;
      fetchPage(nextPage).finally(() => {
        loadingMore.current = false;
      });
    }
  }, [page, totalPages, fetchPage]);

  const renderOrder = useCallback(({item}) => {
    const date = parseDate(item.deliveryDate);
    const amountColor = getAmountColor(item.paymentStatus);
    const statusColor = colors.status[item.status] || colors.info;
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        style={styles.card}
        onPress={() => navigation.navigate('Orders', {screen: 'OrdersTab', params: {screen: 'OrderView', params: {orderId: item.id}}})}>
        <View style={[styles.cardAccent, {backgroundColor: statusColor}]} />
        <View style={styles.cardBody}>
          {/* Top: order number + amount */}
          <View style={styles.topRow}>
            <View style={styles.orderIdRow}>
              <Text style={styles.orderNumber}>{item.orderNumber}</Text>
              {item.urgent && <Icon name="flag" size={14} color={colors.error} style={{marginLeft: 4}} />}
            </View>
            <View style={styles.amountCol}>
              {item.balanceAmount != null && item.totalAmount != null &&
                Number(item.balanceAmount) !== Number(item.totalAmount) && (
                <Text style={styles.totalAmountStrike}>
                  {'\u20B9'}{Number(item.totalAmount || 0).toLocaleString()}
                </Text>
              )}
              <Text style={[styles.balanceAmount, {color: amountColor}]}>
                {'\u20B9'}{Number(item.balanceAmount || 0).toLocaleString()}
              </Text>
            </View>
          </View>
          {/* Bottom: status + date */}
          <View style={styles.bottomRow}>
            <View style={[styles.statusBadge, {backgroundColor: statusColor + '18'}]}>
              <View style={[styles.statusDot, {backgroundColor: statusColor}]} />
              <Text style={[styles.statusText, {color: statusColor}]}>{t(`order.status.${item.status}`)}</Text>
            </View>
            {date && (
              <View style={styles.dateTag}>
                <Icon name="calendar-outline" size={13} color={colors.textSecondary} />
                <Text style={styles.dateText}>{date.day} {date.month}</Text>
              </View>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  }, [navigation, t]);

  return (
    <View style={styles.container}>
      <GradientHeader title={`${customerName} — ${summaryLabel}`} onBack={() => navigation.goBack()} />

      {isLoading && orders.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={orders}
          renderItem={renderOrder}
          keyExtractor={item => String(item.id)}
          contentContainerStyle={styles.listContent}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.3}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Icon name="receipt" size={48} color={colors.textLight} />
              <Text style={styles.emptyText}>{t('common.noData')}</Text>
            </View>
          }
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
          }
        />
      )}

      <View style={styles.footer}>
        <Text style={styles.footerLabel}>{summaryLabel}</Text>
        <Text style={styles.footerAmount}>
          {'\u20B9'}{Number(summaryAmount || 0).toLocaleString('en-IN')}
        </Text>
        <Text style={styles.footerCount}>
          {summaryCount} {summaryCount === 1 ? 'order' : 'orders'}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  listContent: {padding: spacing.sm, paddingBottom: 70, gap: spacing.sm},

  // Card (matches OrderListScreen style)
  card: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: 10,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 2,
  },
  cardAccent: {width: 4},
  cardBody: {flex: 1, padding: spacing.md - 2},
  topRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'},
  orderIdRow: {flexDirection: 'row', alignItems: 'center'},
  orderNumber: {fontWeight: '700', color: colors.primary, fontSize: 14},
  amountCol: {alignItems: 'flex-end'},
  totalAmountStrike: {fontSize: 12, color: colors.textSecondary, textDecorationLine: 'line-through', marginBottom: 1},
  balanceAmount: {fontSize: 15, fontWeight: '700'},
  bottomRow: {flexDirection: 'row', alignItems: 'center', marginTop: 8, gap: 10},
  statusBadge: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6,
  },
  statusDot: {width: 6, height: 6, borderRadius: 3, marginRight: 5},
  statusText: {fontSize: 12, fontWeight: '600'},
  dateTag: {flexDirection: 'row', alignItems: 'center', gap: 4},
  dateText: {fontSize: 12, color: colors.textSecondary, fontWeight: '500'},

  // Empty
  emptyContainer: {alignItems: 'center', paddingTop: spacing.xxl * 2},
  emptyText: {color: colors.textSecondary, marginTop: spacing.sm, fontSize: 14},

  // Footer
  footer: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.sm + 2, paddingHorizontal: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.divider,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: -2},
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 4,
  },
  footerLabel: {flex: 1, fontSize: 13, color: colors.textSecondary, fontWeight: '600'},
  footerAmount: {fontSize: 15, fontWeight: '700', color: colors.primary, marginRight: spacing.md},
  footerCount: {fontSize: 12, color: colors.textSecondary},
});

export default CustomerOrdersScreen;
